import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Avatar,
  useTheme,
  Pagination,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailedIcon,
  Warning as WarningIcon,
  AccountCircle as AccountIcon,
  LocationOn as LocationIcon,
  Computer as DeviceIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import PageContentLayout from "../components/layout/PageContentLayout";

const LoginLogs = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Mock login logs data - Replace with API call
  const loginLogs = [
    {
      id: 1,
      userId: "admin001",
      userName: "John Doe",
      email: "john@eduok.com",
      status: "Success",
      ipAddress: "192.168.1.100",
      location: "Mumbai, India",
      device: "Windows 10 - Chrome",
      loginTime: "2024-01-15 10:30:45",
      logoutTime: "2024-01-15 18:45:20",
      duration: "8h 14m",
    },
    {
      id: 2,
      userId: "tl002",
      userName: "Jane Smith",
      email: "jane@eduok.com",
      status: "Success",
      ipAddress: "192.168.1.101",
      location: "Delhi, India",
      device: "MacOS - Safari",
      loginTime: "2024-01-15 09:15:30",
      logoutTime: null,
      duration: "Active",
    },
    {
      id: 3,
      userId: "trainer003",
      userName: "Mike Johnson",
      email: "mike@eduok.com",
      status: "Failed",
      ipAddress: "192.168.1.102",
      location: "Bangalore, India",
      device: "Windows 11 - Firefox",
      loginTime: "2024-01-15 11:20:15",
      logoutTime: null,
      duration: "-",
      failureReason: "Invalid password",
    },
    {
      id: 4,
      userId: "admin001",
      userName: "John Doe",
      email: "john@eduok.com",
      status: "Success",
      ipAddress: "192.168.1.105",
      location: "Mumbai, India",
      device: "Android - Chrome Mobile",
      loginTime: "2024-01-14 20:10:30",
      logoutTime: "2024-01-14 22:30:15",
      duration: "2h 19m",
    },
    {
      id: 5,
      userId: "tl004",
      userName: "Sarah Williams",
      email: "sarah@eduok.com",
      status: "Success",
      ipAddress: "192.168.1.103",
      location: "Pune, India",
      device: "Windows 10 - Edge",
      loginTime: "2024-01-15 08:00:00",
      logoutTime: null,
      duration: "Active",
    },
  ];

  const filteredLogs = loginLogs.filter((log) => {
    const matchesSearch =
      log.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.ipAddress.includes(searchTerm) ||
      log.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || log.status === statusFilter;
    const matchesUser = userFilter === "all" || log.userId === userFilter;
    return matchesSearch && matchesStatus && matchesUser;
  });

  const paginatedLogs = filteredLogs.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "Success":
        return "success";
      case "Failed":
        return "error";
      case "Warning":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Success":
        return <SuccessIcon fontSize="small" />;
      case "Failed":
        return <FailedIcon fontSize="small" />;
      case "Warning":
        return <WarningIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const handleExport = () => {
    // Implement export functionality
    console.log("Exporting login logs...");
  };

  return (
    <PageContentLayout
      title="Login Logs"
      subtitle="Monitor and track all user login activities"
      breadcrumbs={[
        { label: "Login Logs", icon: <HistoryIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      actions={
        <>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => window.location.reload()} sx={{ textTransform: "none" }}>
            Refresh
          </Button>
          <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={handleExport} sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
            Export
          </Button>
        </>
      }
    >
        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1, minWidth: 250 }}
              />
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Success">Success</MenuItem>
                  <MenuItem value="Failed">Failed</MenuItem>
                  <MenuItem value="Warning">Warning</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>User</InputLabel>
                <Select
                  value={userFilter}
                  label="User"
                  onChange={(e) => setUserFilter(e.target.value)}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  {[...new Set(loginLogs.map((log) => log.userId))].map((userId) => (
                    <MenuItem key={userId} value={userId}>
                      {userId}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {/* Login Logs Table */}
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Login Time</TableCell>
                    <TableCell>Logout Time</TableCell>
                    <TableCell>Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          No login logs found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                              <AccountIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {log.userName}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {log.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(log.status)}
                            label={log.status}
                            size="small"
                            color={getStatusColor(log.status)}
                          />
                          {log.failureReason && (
                            <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
                              {log.failureReason}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {log.ipAddress}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LocationIcon fontSize="small" sx={{ color: "text.secondary" }} />
                            <Typography variant="body2">{log.location}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <DeviceIcon fontSize="small" sx={{ color: "text.secondary" }} />
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {log.device}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{log.loginTime}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: log.logoutTime ? "text.primary" : "text.secondary" }}>
                            {log.logoutTime || "Active"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.duration}
                            size="small"
                            color={log.duration === "Active" ? "success" : "default"}
                            variant={log.duration === "Active" ? "filled" : "outlined"}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {filteredLogs.length > rowsPerPage && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={Math.ceil(filteredLogs.length / rowsPerPage)}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </CardContent>
        </Card>
    </PageContentLayout>
  );
};

export default LoginLogs;

